/**
 * Portal API
 * Domain API via HTTP transport (apiTransport) to the backend /api RPC.
 */

import {
  Payment,
  TenantDocument,
  Announcement,
  IntegrationSettings,
  type PortalPaymentStatusPayload,
  type TenantFinancialSummary,
} from '@repo/shared-types';
import { apiTransport as router } from './transport';
import { getContext } from './core';

export const portalApi = {
  payments: {
    list: async (tenantId: string) => {
      const context = getContext();
      return router.get<Payment[]>(`/portal/payments?tenantId=${tenantId}`, context);
    },
    get: async (id: string) => {
      const context = getContext();
      return router.get<Payment>(`/portal/payments/${id}`, context);
    },
    getStatus: async (id: string) => {
      const context = getContext();
      return router.get<PortalPaymentStatusPayload>(`/portal/payments/${id}/status`, context);
    },
    create: async (payment: { tenantId: string; amount: number; method: string; date: string; description?: string; receivableId?: string }) => {
      const context = getContext();
      return router.post<Payment>('/portal/payments', payment, context);
    },
    update: async (id: string, updates: Partial<Payment>) => {
      const context = getContext();
      return router.patch<Payment>(`/portal/payments/${id}`, updates, context);
    },
    refund: async (id: string, amount?: number, reason?: string) => {
      const context = getContext();
      return router.post<Payment>(`/portal/payments/${id}/refund`, { amount, reason }, context);
    },
    delete: async (id: string) => {
      const context = getContext();
      return router.delete<{ success: boolean }>(`/portal/payments/${id}`, context);
    }
  },
  wallet: {
    financialSummary: async (tenantId: string) => {
      const context = getContext();
      const q = new URLSearchParams({ tenantId });
      return router.get<TenantFinancialSummary>(`/portal/wallet/financial-summary?${q.toString()}`, context);
    },
  },
  documents: {
    list: async (tenantId: string) => {
      const context = getContext();
      return router.get<TenantDocument[]>(`/portal/documents?tenantId=${tenantId}`, context);
    },
    get: async (id: string) => {
      const context = getContext();
      return router.get<TenantDocument>(`/portal/documents/${id}`, context);
    },
    create: async (doc: { tenantId: string; name: string; type: string; url: string; uploadedAt: string; checklistItemId?: string; sourceApplicationId?: string }) => {
      const context = getContext();
      return router.post<TenantDocument>('/portal/documents', doc, context);
    },
    update: async (id: string, updates: Partial<TenantDocument>) => {
      const context = getContext();
      return router.patch<TenantDocument>(`/portal/documents/${id}`, updates, context);
    },
    delete: async (id: string) => {
      const context = getContext();
      return router.delete<{ success: boolean }>(`/portal/documents/${id}`, context);
    }
  },
  announcements: {
    list: async () => {
      const context = getContext();
      return router.get<Announcement[]>('/portal/announcements', context);
    },
    get: async (id: string) => {
      const context = getContext();
      return router.get<Announcement>(`/portal/announcements/${id}`, context);
    },
    create: async (announcement: Announcement) => {
      const context = getContext();
      return router.post<Announcement>('/portal/announcements', announcement, context);
    },
    update: async (id: string, updates: Partial<Announcement>) => {
      const context = getContext();
      return router.patch<Announcement>(`/portal/announcements/${id}`, updates, context);
    },
    delete: async (id: string) => {
      const context = getContext();
      return router.delete<{ success: boolean }>(`/portal/announcements/${id}`, context);
    }
  },
  integrations: {
    getByType: async (type: string) => {
      const context = getContext();
      return router.get<IntegrationSettings>(`/portal/integrations/${type}`, context);
    },
  },
  owner: {
    stripeConnectAccountLink: async (body: { refreshUrl: string; returnUrl: string }) => {
      const context = getContext();
      return router.post<{ url: string }>('/portal/owner/stripe-connect/account-link', body, context);
    },
    /** Stripe Connect Embedded Components — Account Session client secret (in-app onboarding / management). */
    stripeConnectAccountSession: async () => {
      const context = getContext();
      return router.post<{ clientSecret: string }>('/portal/owner/stripe-connect/account-session', {}, context);
    },
  },
  vendor: {
    /** Stripe Connect hosted onboarding first (KYC/AML); then link payout bank (Plaid) per product roadmap. */
    stripeConnectAccountLink: async (body: { refreshUrl: string; returnUrl: string }) => {
      const context = getContext();
      return router.post<{ url: string }>('/portal/vendor/stripe-connect/account-link', body, context);
    },
    stripeConnectAccountSession: async () => {
      const context = getContext();
      return router.post<{ clientSecret: string }>('/portal/vendor/stripe-connect/account-session', {}, context);
    },
  },
  saas: {
    stripeCheckoutSession: async (body: { priceId: string; successUrl: string; cancelUrl: string }) => {
      const context = getContext();
      return router.post<{ url: string | null }>('/portal/saas/stripe/checkout-session', body, context);
    },
  },
};

